// --------------------------------------------------
// ⚙️ Setup
// --------------------------------------------------

import gsap from "gsap";
import ScrollToPlugin from "gsap/ScrollToPlugin";
import Photoswipe from "photoswipe";
import { Splide } from "@splidejs/splide";
import "media-chrome";

gsap.registerPlugin(ScrollToPlugin);

// --------------------------------------------------
// ♿ Accessibility
// --------------------------------------------------

let a11yModes = localStorage.getItem("a11y-modes");

let a11yModeButtons = document.querySelectorAll(".a11y-mode");
a11yModeButtons.forEach((a11yModeButton) => {
  let mode = a11yModeButton.dataset.a11yMode;
  if (a11yModes && a11yModes.includes(mode)) {
    a11yModeButton.setAttribute("aria-pressed", "true");

    gsap.set(a11yModeButton.querySelector(".dot"), {
      x: 48,
    });

    document.documentElement.classList.add(mode);
  }

  a11yModeButton.addEventListener("click", (e) => {
    if (a11yModeButton.getAttribute("aria-pressed") == "true") {
      a11yModeButton.setAttribute("aria-pressed", "false");

      gsap.fromTo(
        a11yModeButton.querySelector(".dot"),
        {
          x: 48,
        },
        {
          x: 0,
          ease: "power4.out",
          duration: 0.25,
        }
      );

      document.documentElement.classList.remove(mode);
      a11yModes = a11yModes.replace(mode, "");

      document.documentElement.dispatchEvent(new Event("a11yModesChanged"));
    } else {
      a11yModeButton.setAttribute("aria-pressed", "true");

      gsap.fromTo(
        a11yModeButton.querySelector(".dot"),
        {
          x: 0,
        },
        {
          x: 48,
          ease: "power4.out",
          duration: 0.25,
        }
      );

      document.documentElement.classList.add(mode);
      a11yModes += mode;
    }

    localStorage.setItem("a11y-modes", a11yModes);

    document.documentElement.dispatchEvent(new Event("a11yModesChanged"));
  });
});

// Automatically enable high contrast mode if the user has it enabled in their OS
if (window.matchMedia("(prefers-contrast: more)").matches) {
  let highContrastToggle = document.querySelector(".a11y-mode--high-contrast");
  if (highContrastToggle.getAttribute("aria-pressed") == "false") {
    highContrastToggle.dispatchEvent(new Event("click"));
  }
}

// Enable low stimulus mode if the user has it enabled in their OS
if (window.matchMedia("(prefers-reduced-motion)").matches) {
  let lowStimulusToggle = document.querySelector(".a11y-mode--low-stimulus");
  if (lowStimulusToggle.getAttribute("aria-pressed") == "false") {
    lowStimulusToggle.dispatchEvent(new Event("click"));
  }
}

let headlineElements = document.querySelectorAll(
  ".headline-block--with-image h1, .headline-block--with-image h2, .headline-block--with-image p, .media-block--contrast h2, .media-block--contrast p"
);

const splitHeadlineLines = () => {
  headlineElements.forEach((headlineElement) => {
    if (
      (a11yModes &&
        a11yModes.includes("high-contrast") &&
        !a11yModes.includes("low-stimulus")) ||
      headlineElement
        .closest(".headline-block")
        ?.classList.contains("headline-block--contrast") ||
      headlineElement
        .closest(".media-block")
        ?.classList.contains("media-block--contrast")
    ) {
      // Wrap content of element in div
      let headlineElementContent = headlineElement.innerHTML;
      let markedLine = headlineElement.querySelector(".marked-line");
      if (!markedLine) {
        headlineElement.innerHTML =
          '<div class="marked-line">' + headlineElementContent + "</div>";
      }
    } else {
      // Revert to original content
      let markedLine = headlineElement.querySelector(".marked-line");
      if (markedLine) {
        headlineElement.innerHTML = markedLine.innerHTML;
      }
    }
  });
};

splitHeadlineLines();

document.documentElement.addEventListener("a11yModesChanged", () => {
  splitHeadlineLines();
});

// Add aria-label for media-controller elements because we can't override the hardcoded aria-label
const mediaControllers = document.querySelectorAll("media-controller");
mediaControllers.forEach((mediaController) => {
  mediaController.setAttribute("aria-label", mediaController.title);
  mediaController.removeAttribute("title");
});

// --------------------------------------------------
// 🧈 Sticky Header
// --------------------------------------------------

const header = document.querySelector(".header");

if (
  header &&
  ["home", "landing_page"].includes(document.documentElement.dataset.template)
) {
  const backgroundColor = getComputedStyle(document.body).getPropertyValue(
    "--color-black"
  );
  const color = getComputedStyle(document.body).getPropertyValue(
    "--color-white"
  );

  // Store initial colors
  const initialBackgroundColor = getComputedStyle(header).backgroundColor;
  const initialColor = getComputedStyle(header).color;
  let isColored = false;

  window.addEventListener("scroll", () => {
    const shouldBeColored = window.scrollY > 0;

    if (shouldBeColored !== isColored) {
      isColored = shouldBeColored;

      gsap.to(header, {
        color: isColored ? color : initialColor,
        backgroundColor: isColored ? backgroundColor : initialBackgroundColor,
        duration: 0.25,
      });
    }
  });
}

// --------------------------------------------------
// ↔️ Marquees
// --------------------------------------------------

let marquees = document.querySelectorAll(".ticker-block--marquee");
marquees.forEach((marquee) => {
  let marqueeText = marquee.querySelector(".ticker-block__text");

  for (let i = 0; i < 5; i++) {
    let marqueeTextClone = marqueeText.cloneNode(true);
    marqueeTextClone.classList.add("ticker-block__text--clone");
    marqueeTextClone.setAttribute("aria-hidden", "true");
    marquee.appendChild(marqueeTextClone);
  }

  let marqueeTexts = marquee.querySelectorAll(".ticker-block__text");

  let infinity = gsap.to(marqueeTexts, {
    xPercent: -100,
    duration: 5,
    ease: "none",
    repeat: -1,
  });

  marquee.addEventListener("mouseenter", function () {
    gsap.to(infinity, {
      timeScale: 0,
      ease: "power1.out",
    });
  });
  marquee.addEventListener("mouseleave", function () {
    gsap.to(infinity, {
      timeScale: 1,
      ease: "power1.out",
    });
  });
});

// --------------------------------------------------
// ⬇️ Overlay
// --------------------------------------------------

// Initialize all dialogs
const dialogs = document.querySelectorAll("dialog.overlay");

dialogs.forEach((dialog) => {
  // DOM references
  const toggleButtons = document.querySelectorAll(
    `[aria-controls="${dialog.id}"]`
  );
  const closeButton = dialog.querySelector(".overlay__close");

  // Open/Close Functions
  const openDialog = () => {
    dialog.showModal();

    // Apply animation if reduced motion is not preferred
    if (!document.documentElement.classList.contains("low-stimulus")) {
      dialog.animate(
        [
          { clipPath: "polygon(0 0, 100% 0, 100% 0%, 0 0%)" },
          { clipPath: "polygon(0 0, 100% 0, 100% 100%, 0 100%)" },
        ],
        {
          duration: 500,
          easing: "ease-in-out",
        }
      );
    }
  };

  const closeDialog = () => {
    if (document.documentElement.classList.contains("low-stimulus")) {
      dialog.close();
      return;
    }

    const animation = dialog.animate(
      [
        { clipPath: "polygon(0 0, 100% 0, 100% 100%, 0 100%)" },
        { clipPath: "polygon(0 0, 100% 0, 100% 0%, 0 0%)" },
      ],
      {
        duration: 500,
        easing: "ease-in-out",
      }
    );

    animation.onfinish = () => {
      dialog.close();
    };
  };

  // Event Listeners
  toggleButtons.forEach((btn) => btn.addEventListener("click", openDialog));
  closeButton.addEventListener("click", closeDialog);

  // Close on backdrop click
  dialog.addEventListener("click", (e) => {
    if (e.target === dialog) closeDialog();
  });
});

/* -------------------------------------------------- */
/* 🪗 Accordion */
/* -------------------------------------------------- */

// Select all accordion elements
const accordions = document.querySelectorAll(".accordion");

// Iterate over each accordion
accordions.forEach((accordion) => {
  // Select the title and content of the accordion item
  let title = accordion.querySelector(".accordion__title");
  let arrow = title.querySelector("svg");
  let content = accordion.querySelector(".accordion__content");

  const openAccordion = () => {
    gsap
      .timeline({
        defaults: {
          duration: document.documentElement.classList.contains("low-stimulus")
            ? 0
            : 0.5,
          ease: "power3.out",
        },
      })
      .set(content, {
        display: "block",
      })
      .to(
        content,
        {
          height: "auto",
        },
        0
      )
      .to(
        arrow,
        {
          rotate: -180,
        },
        0
      );
  };

  const closeAccordion = () => {
    gsap
      .timeline({
        defaults: {
          duration: document.documentElement.classList.contains("low-stimulus")
            ? 0
            : 0.5,
          ease: "power3.out",
        },
      })
      .to(
        content,
        {
          height: 0,
        },
        0
      )
      .to(
        arrow,
        {
          rotate: 0,
        },
        0
      )
      .set(content, {
        display: "none",
      });
  };

  // Add a click event listener to the title
  title.addEventListener("click", (e) => {
    if (gsap.isTweening(content)) return;

    // Check if the accordion is expanded
    const expanded = title.getAttribute("aria-expanded") === "true";

    // Toggle the aria-expanded attribute
    title.setAttribute("aria-expanded", !expanded);

    if (!expanded) {
      // Play the animation or apply changes instantly
      openAccordion();

      // Update the URL with the accordion's data-href
      history.replaceState(null, null, title.dataset.href);
    } else {
      // Reverse the animation or apply changes instantly
      closeAccordion();

      // Update the URL to remove the accordion ID
      history.replaceState(null, null, "#!");
    }
  });
});

// Handle initial hash state
if (window.location.hash) {
  const accordion = document.querySelector(
    `[data-id="${window.location.hash.substring(1)}"]`
  );

  if (accordion) {
    accordion
      .querySelector(".accordion__title")
      .dispatchEvent(new Event("click"));

    accordion.scrollIntoView();
  }
}

// --------------------------------------------------
// ➕ Dates More Button
// --------------------------------------------------

let moreButtons = document.querySelectorAll(".dates__more");
moreButtons.forEach(function (moreButton) {
  let dates = moreButton.closest(".dates");
  let datesTeaser = dates.querySelector(".dates__teaser");

  let tl = gsap
    .timeline({
      paused: true,
      defaults: {
        ease: "power1.inOut",
        duration: 0.5,
      },
    })
    .to(
      datesTeaser,
      {
        height: "auto",
        maskImage: "linear-gradient(to bottom, black 50%, black 100%)",
      },
      0
    );

  moreButton.addEventListener("click", (e) => {
    moreButton.setAttribute(
      "aria-expanded",
      moreButton.getAttribute("aria-expanded") === "false" ? "true" : "false"
    );

    if (moreButton.getAttribute("aria-expanded") === "true") {
      tl.play();
    } else {
      tl.reverse();
    }
  });
});

// --------------------------------------------------
// 📩 Newsletter Form
// --------------------------------------------------

let newsletterForm = document.querySelector(".newsletterli-form");

if (newsletterForm) {
  newsletterForm.addEventListener("submit", (e) => {
    fetch(newsletterForm.action, {
      method: "POST",
      body: new FormData(newsletterForm),
    })
      .then(function (response) {
        document.querySelector(".newsletterli-form__fields").style.display =
          "none";
        document.querySelector(".newsletterli-form__success").style.display =
          "block";
      })
      .catch(function (error) {
        console.log("Error", error);
      });

    e.preventDefault();
  });
}

// --------------------------------------------------
// ♿ Accessibility
// --------------------------------------------------

// Modes

let textWraps = document.querySelectorAll(".text-wrap");
textWraps.forEach((textWrap) => {
  // Add role="presentation" to all br elements to skip them in Chrome
  let brElements = textWrap.querySelectorAll("br");
  brElements.forEach((brElement) => {
    brElement.setAttribute("role", "presentation");
  });
});

// Common configuration for Photoswipe lightbox
const LIGHTBOX_CONFIG = {
  showHideAnimationType: "none",
  bgOpacity: 1,
  zoom: false,
  close: false,
};

// SVG close button icon - extracted to avoid duplication
const CLOSE_BUTTON_SVG = `<svg aria-hidden="true" width="47" height="48" viewBox="0 0 47 48" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M19.0919 24L0 43.0919L4.24264 47.3345L23.3345 28.2426L42.4264 47.3345L46.669 43.0919L27.5772 24L46.669 4.90811L42.4264 0.665466L23.3345 19.7573L4.24264 0.665468L0 4.90811L19.0919 24Z" fill="white"/></svg>`;

/**
 * Creates a custom close button element for the lightbox
 * @param {Object} toggle - The toggle element with dataset properties
 * @param {Object} lightbox - The Photoswipe instance
 */
function createCloseButton(toggle, lightbox) {
  lightbox.ui.registerElement({
    name: "custom-close-button",
    tagName: "button",
    html: `${CLOSE_BUTTON_SVG}${toggle.dataset.close}`,
    onClick: () => lightbox.close(),
  });
}

/**
 * Initializes a lightbox for the given toggle element
 * @param {Element} toggle - The toggle element
 * @param {Object} dataSource - The data source configuration for Photoswipe
 */
function initializeLightbox(toggle, dataSource) {
  const lightbox = new Photoswipe({
    ...LIGHTBOX_CONFIG,
    dataSource: [dataSource],
  });

  lightbox.on("uiRegister", function () {
    // Add close button
    createCloseButton(toggle, lightbox);

    // Add caption for images if alt text exists
    if (toggle.classList.contains("image-toggle") && toggle.dataset.alt) {
      lightbox.ui.registerElement({
        name: "custom-caption",
        order: 9,
        isButton: false,
        appendTo: "root",
        html: toggle.dataset.alt,
      });
    }
  });

  lightbox.init();
}

// Handle all toggles
document.querySelectorAll(".image-toggle, .video-toggle").forEach((toggle) => {
  toggle.addEventListener("click", () => {
    const dataSource = toggle.classList.contains("image-toggle")
      ? {
          // Image configuration
          src: toggle.dataset.image,
          width: toggle.dataset.width,
          height: toggle.dataset.height,
          alt: toggle.dataset.alt,
        }
      : {
          // Video configuration
          html: toggle.closest(".video-block").querySelector("media-controller")
            .outerHTML,
          type: "html",
        };

    initializeLightbox(toggle, dataSource);
  });
});

// --------------------------------------------------
// 💧 Slider
// --------------------------------------------------

const sliders = () => {
  const sliderWrappers = document.querySelectorAll(".slider.splide");

  let i18n = {
    prev: "Vorheriger Slide",
    next: "Nächster Slide",
    select: "Wähle einen Slide aus",
    slideX: "Gehe zu Slide %s",
    carousel: "Slideshow",
    slide: "Slide",
    slideLabel: "%s von %s",
  };
  if (document.documentElement.lang === "en") {
    i18n = {
      prev: "Previous slide",
      next: "Next slide",
      select: "Select a slide to show",
      slideX: "Go to slide %s",
      carousel: "Slideshow",
      slide: "Slide",
      slideLabel: "%s of %s",
    };
  }

  sliderWrappers.forEach((sliderWrapper) => {
    let splide = new Splide(sliderWrapper, {
      type: "loop",
      perPage: 1,
      pagination: true,
      arrows: true,
      dragMinThreshold: {
        mouse: 0,
        touch: 10,
      },
      flickPower: 20000,
      flickMaxPages: 1,
      arrowPath:
        "M38.5432 21.4568L40 19.9999L38.5432 18.543L23.434 3.43384L20.5202 6.34761L32.112 17.9394H0V22.0601H32.1123L20.5202 33.6522L23.434 36.566L38.5432 21.4568Z",
      i18n,
      speed: 400,
    });

    splide.mount();

    let adjustSpeed = () => {
      if (a11yModes && a11yModes.includes("low-stimulus")) {
        splide.options.speed = 0;
      } else {
        splide.options.speed = 400;
      }
    };

    adjustSpeed();

    document.documentElement.addEventListener("a11yModesChanged", () => {
      adjustSpeed();
    });
  });
};

sliders();

// --------------------------------------------------
// Events search
// --------------------------------------------------

let eventsSearch = document.querySelector(".events-search");

if (eventsSearch) {
  const eventsSearchInputWrap = eventsSearch.querySelector(
    ".events-search__input-wrap"
  );
  const eventsSearchInput = eventsSearch.querySelector(".events-search__input");
  const eventsSearchButton = eventsSearch.querySelector(
    ".events-search__button"
  );
  const eventsBlock = document.querySelector(".events-block");

  eventsBlock.setAttribute("tabindex", "-1");

  eventsSearchButton.addEventListener("click", (e) => {
    let eventSearchHeight = eventsSearchInputWrap.offsetHeight;

    let duration = 0.5;
    if (document.documentElement.classList.contains("low-stimulus")) {
      duration = 0;
    }

    if (eventsSearchButton.getAttribute("aria-expanded") === "true") {
      eventsSearchButton.setAttribute("aria-expanded", "false");
      eventsSearchInputWrap.setAttribute("aria-hidden", "true");

      eventsSearchInput.value = "";
      filterEvents();

      gsap.fromTo(
        eventsSearchInputWrap,
        {
          y: 0,
          autoAlpha: 1,
        },
        {
          ease: "power3.out",
          y: () => eventSearchHeight * -1,
          duration,
          autoAlpha: 0,
        }
      );

      gsap.fromTo(
        ".events-block",
        {
          marginTop: () => eventSearchHeight,
        },
        {
          ease: "power3.out",
          duration,
          marginTop: 0,
        }
      );
    } else {
      eventsSearchButton.setAttribute("aria-expanded", "true");
      eventsSearchInputWrap.setAttribute("aria-hidden", "false");

      gsap.fromTo(
        eventsSearchInputWrap,
        {
          y: () => eventSearchHeight * -1,
          autoAlpha: 0,
        },
        {
          ease: "power3.out",
          y: 0,
          autoAlpha: 1,
          duration,
        }
      );

      gsap.delayedCall(0.1, () => {
        eventsSearchInput.focus();
      });

      gsap.fromTo(
        ".events-block",
        {
          marginTop: 0,
        },
        {
          ease: "power3.out",
          marginTop: () => eventSearchHeight,
          duration,
        }
      );
    }
  });

  function filterEvents() {
    let months = document.querySelectorAll(".month");
    let events = document.querySelectorAll(".event-link");
    let searchValue = eventsSearchInput.value.toLowerCase();
    let noResults = document.querySelector(".events-block__no-results");

    // Hide events
    events.forEach((event) => {
      // Text
      let eventText = event
        .querySelector(".event-link__title")
        .innerText.toLowerCase();

      // Date
      let date = event.querySelector(".date").getAttribute("aria-label");
      if (date) {
        eventText += date.toLowerCase();
      }

      // Hidden text
      let hiddenText = event.querySelector(".event-link__hidden-text");
      if (hiddenText) {
        eventText += hiddenText.innerText.toLowerCase();
      }

      // A11y features
      let eventA11yFeatures = event.querySelectorAll(".a11y-feature");
      eventA11yFeatures.forEach((a11yFeature) => {
        eventText += a11yFeature.getAttribute("title").toLowerCase();
      });

      if (eventText.includes(searchValue)) {
        event.setAttribute("aria-hidden", "false");
      } else {
        event.setAttribute("aria-hidden", "true");
      }
    });

    // Hide empty months
    months.forEach((month) => {
      let eventsInMonth = month.querySelectorAll(
        '.event-link[aria-hidden="false"]'
      );

      if (eventsInMonth.length > 0) {
        month.setAttribute("aria-hidden", "false");
      } else {
        month.setAttribute("aria-hidden", "true");
      }
    });

    // Handle empty search
    if (
      document.querySelectorAll('.event-link[aria-hidden="false"]').length === 0
    ) {
      noResults.setAttribute("aria-hidden", "false");
      gsap.set(noResults, {
        display: "flex",
      });
    } else {
      noResults.setAttribute("aria-hidden", "true");
      gsap.set(noResults, {
        display: "none",
      });
    }
  }

  eventsSearchInput.addEventListener("input", (e) => {
    filterEvents();
  });

  eventsSearchInput.addEventListener("keypress", function (e) {
    if (e.key == "Enter") {
      eventsBlock.focus();
    }
  });
}

// --------------------------------------------------
// 📺 Screens
// --------------------------------------------------

if (document.documentElement.dataset.template == "tv") {
  // Disable scrolling via scroll wheel
  document.addEventListener(
    "wheel",
    (e) => {
      e.preventDefault();
    },
    { passive: false }
  );

  let blocks = document.querySelectorAll(".block--tv");

  let activeBlock = null;
  let delayedCall = null;
  let tween = null;
  let activeVideo = null;

  const nextBlock = (instant) => {
    activeBlock = activeBlock?.nextElementSibling || blocks[0];

    let nextVideo = activeBlock.querySelector("video");

    gsap.set(activeBlock, {
      scrollTo: {
        y: 0,
      },
    });

    gsap.to(window, {
      duration: instant ? 0 : 1,
      scrollTo: {
        y: activeBlock,
      },
      ease: "power2.inOut",
      onComplete: () => {
        if (activeBlock.matches(":last-child")) {
          gsap.set(window, {
            scrollTo: 0,
          });
          window.location.reload();
        }

        // Pause and reset active video
        if (activeVideo) {
          activeVideo.pause();
          activeVideo.currentTime = 0;
        }
        // Play next video
        if (nextVideo) {
          nextVideo.play();
          activeVideo = nextVideo;
        }

        // Scroll element
        if (!nextVideo && activeBlock.scrollHeight > activeBlock.clientHeight) {
          tween = gsap.to(activeBlock, {
            duration: activeBlock.dataset.duration - 2,
            scrollTo: {
              y: "max",
            },
            delay: 2,
            ease: "none",
            onComplete: () => {
              gsap.delayedCall(2, nextBlock);
            },
          });
        }

        // Animate to next block after duration
        else {
          delayedCall = gsap.delayedCall(
            activeBlock.dataset.duration,
            nextBlock
          );
        }
      },
    });
  };

  nextBlock();

  document.documentElement.addEventListener("click", () => {
    delayedCall?.kill();
    tween?.kill();
    nextBlock();
  });

  document.documentElement.addEventListener("keydown", (e) => {
    // If space or arrow down is pressed
    if (e.key == " " || e.key == "ArrowDown") {
      delayedCall?.kill();
      tween?.kill();
      nextBlock();
      e.preventDefault();
    }
  });
}

// --------------------------------------------------
// :has polyfill
// --------------------------------------------------

let headlineBlocks = document.querySelectorAll(".headline-block");

headlineBlocks.forEach((headlineBlock) => {
  let headlineBlockLink = headlineBlock.querySelector(".headline-block__link");

  if (headlineBlockLink) {
    headlineBlock.classList.add("headline-block--has-link");
  }
});

// --------------------------------------------------
// Make call to action modules clickable
// --------------------------------------------------

let blockButtons = document.querySelectorAll(".block > a.button");

blockButtons.forEach((blockButton) => {
  let block = blockButton.closest(".block");
  let otherLinks = block.querySelectorAll("a:not(.button)");

  if (!otherLinks.length) {
    block.dataset.clickable = true;

    block.addEventListener("click", (e) => {
      if (!e.target.matches("a, a > *")) {
        window.location = blockButton.getAttribute("href");
      }
    });

    block.addEventListener("mouseenter", (e) => {
      blockButton.dataset.hover = true;
    });
    block.addEventListener("mouseleave", (e) => {
      blockButton.dataset.hover = false;
    });
  }
});
